import {
	closestCenter,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	useDroppable,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragOverEvent,
	type DragStartEvent,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyleSheet, View } from 'react-native';

import { FlexModal } from '@/components/flex-modal';
import { ColumnPickerCloseConfirm } from '@/components/reports/column-picker-close-confirm';
import { ColumnPresetBar } from '@/components/reports/column-preset-bar';
import { Button, IconButton, Searchbar, Text, useTheme } from '@/components/ui';
import type { ColumnLayout } from '@/hooks/use-column-layout';
import type { ColumnPreset } from '@/hooks/use-column-presets';
import { spacing } from '@/styles/theme';

/**
 * Web column picker. Uses @dnd-kit for a polished drag experience:
 *
 *   - The picked-up row appears in a `DragOverlay` that follows the cursor
 *     at full opacity; the source slot renders at 0.4 opacity so it looks
 *     like the row was literally pulled OUT of its spot.
 *   - Each panel is its own `SortableContext` (vertical list). Reorders
 *     inside a list animate; cross-list drops (selected↔available) are
 *     handled in `onDragOver`/`onDragEnd`.
 *   - Panel bodies are also `useDroppable` so an empty panel shows a
 *     clear "Drop here" target, and rows dropped on the panel body land
 *     at the end of the list.
 *
 * Native fallback (`column-picker.tsx`) has click-to-move buttons only —
 * this file intentionally skips the native fallback because reports are
 * web-only.
 */

export interface ColumnChoice {
	id: string;
	label: string;
}

type Section = 'selected' | 'available';

interface ColumnPickerProps {
	visible: boolean;
	onDismiss: () => void;
	columns: ColumnChoice[];
	layout: ColumnLayout;
	onApply: (next: ColumnLayout) => void;
	onReset: () => void;
	presets?: ColumnPreset[];
	onSavePreset?: (name: string, layout: ColumnLayout) => void;
	onDeletePreset?: (presetId: string) => void;
}

/** Opaque id prefix we attach to droppable containers so we can tell
 *  them apart from row ids (which are just the column ids). */
const CONTAINER_PREFIX = 'container:';
const containerId = (section: Section) => `${CONTAINER_PREFIX}${section}`;
const isContainerId = (id: string) => id.startsWith(CONTAINER_PREFIX);
const parseContainerId = (id: string): Section => id.slice(CONTAINER_PREFIX.length) as Section;

export const ColumnPicker: React.FC<ColumnPickerProps> = ({
	visible,
	onDismiss,
	columns,
	layout,
	onApply,
	onReset,
	presets,
	onSavePreset,
	onDeletePreset,
}) => {
	const theme = useTheme();
	const [selected, setSelected] = useState<string[]>(layout.visible);
	const [available, setAvailable] = useState<string[]>(layout.hidden);
	const [search, setSearch] = useState('');
	const [activeId, setActiveId] = useState<string | null>(null);
	const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);

	useEffect(() => {
		if (visible) {
			setSelected(layout.visible);
			setAvailable(layout.hidden);
			setSearch('');
			setActiveId(null);
			setCloseConfirmVisible(false);
		}
	}, [visible, layout.visible, layout.hidden]);

	const labelFor = useMemo(() => {
		const map = new Map<string, string>();
		for (const c of columns) map.set(c.id, c.label);
		return (id: string) => map.get(id) ?? id;
	}, [columns]);

	const filteredAvailable = useMemo(() => {
		if (!search.trim()) return available;
		const q = search.toLowerCase();
		return available.filter(id => labelFor(id).toLowerCase().includes(q));
	}, [available, search, labelFor]);

	const sensors = useSensors(
		// 4px activation distance so a plain click on the arrow button or
		// the grip icon doesn't start a drag.
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const findSection = (id: string): Section | null => {
		if (selected.includes(id)) return 'selected';
		if (available.includes(id)) return 'available';
		return null;
	};

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(String(event.active.id));
	};

	const handleDragOver = (event: DragOverEvent) => {
		const { active, over } = event;
		if (!over) return;

		const activeIdStr = String(active.id);
		const overIdStr = String(over.id);

		const activeSection = findSection(activeIdStr);
		if (!activeSection) return;

		// Figure out which section the user is hovering over. It could be
		// a row (same list or other list) or a container (empty panel).
		const overSection: Section | null = isContainerId(overIdStr)
			? parseContainerId(overIdStr)
			: findSection(overIdStr);
		if (!overSection || overSection === activeSection) return;

		// Cross-section move: transfer the id live so the user sees the
		// row appear in the target list while still dragging (dnd-kit
		// recommendation for multi-container sortables).
		if (activeSection === 'selected') {
			setSelected(prev => prev.filter(x => x !== activeIdStr));
			setAvailable(prev => {
				if (prev.includes(activeIdStr)) return prev;
				const overIndex = isContainerId(overIdStr)
					? prev.length
					: prev.indexOf(overIdStr);
				const insertAt = overIndex >= 0 ? overIndex : prev.length;
				const next = [...prev];
				next.splice(insertAt, 0, activeIdStr);
				return next;
			});
		} else {
			setAvailable(prev => prev.filter(x => x !== activeIdStr));
			setSelected(prev => {
				if (prev.includes(activeIdStr)) return prev;
				const overIndex = isContainerId(overIdStr)
					? prev.length
					: prev.indexOf(overIdStr);
				const insertAt = overIndex >= 0 ? overIndex : prev.length;
				const next = [...prev];
				next.splice(insertAt, 0, activeIdStr);
				return next;
			});
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveId(null);
		if (!over) return;

		const activeIdStr = String(active.id);
		const overIdStr = String(over.id);

		// After onDragOver has already moved cross-section, we only need
		// to handle intra-section reorders here.
		const activeSection = findSection(activeIdStr);
		if (!activeSection) return;

		const list = activeSection === 'selected' ? selected : available;
		const setList = activeSection === 'selected' ? setSelected : setAvailable;

		if (isContainerId(overIdStr)) return;
		const activeIndex = list.indexOf(activeIdStr);
		const overIndex = list.indexOf(overIdStr);
		if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) return;

		setList(arrayMove(list, activeIndex, overIndex));
	};

	const handleApply = () => {
		onApply({ visible: selected, hidden: available });
		onDismiss();
	};

	const handleUseDefaults = () => {
		onReset();
		onDismiss();
	};

	const isDirty = useMemo(() => {
		if (selected.length !== layout.visible.length) return true;
		if (available.length !== layout.hidden.length) return true;
		for (let i = 0; i < selected.length; i++) {
			if (selected[i] !== layout.visible[i]) return true;
		}
		for (let i = 0; i < available.length; i++) {
			if (available[i] !== layout.hidden[i]) return true;
		}
		return false;
	}, [selected, available, layout.visible, layout.hidden]);

	const handleRequestClose = () => {
		if (!isDirty) {
			onDismiss();
			return;
		}
		// Inline nested Modal — AlertProvider's web dialog portals at the same
		// level as FlexModal and renders behind it, so we own the confirm UI.
		setCloseConfirmVisible(true);
	};

	const activeLabel = activeId ? labelFor(activeId) : '';

	const moveToAvailable = (id: string) => {
		setSelected(prev => prev.filter(x => x !== id));
		setAvailable(prev => (prev.includes(id) ? prev : [...prev, id]));
	};

	const moveToSelected = (id: string) => {
		setAvailable(prev => prev.filter(x => x !== id));
		setSelected(prev => (prev.includes(id) ? prev : [...prev, id]));
	};

	return (
		<FlexModal visible={visible} onDismiss={handleRequestClose}>
			<View style={styles.header}>
				<Text style={[styles.title, { color: theme.colors.onSurface }]}>
					Choose which columns you see
				</Text>
				<IconButton icon="close" size={22} onPress={handleRequestClose} accessibilityLabel="Close" />
			</View>

			<View style={styles.searchRow}>
				<Searchbar
					value={search}
					onChangeText={setSearch}
					placeholder="Search available columns..."
					testID="column-picker-search"
				/>
			</View>

			{presets && onSavePreset && onDeletePreset && (
				<ColumnPresetBar
					presets={presets}
					onApply={(p) => {
						setSelected(p.layout.visible);
						setAvailable(p.layout.hidden);
					}}
					onSave={(name) => onSavePreset(name, { visible: selected, hidden: available })}
					onDelete={(p) => onDeletePreset(p.id)}
				/>
			)}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={() => setActiveId(null)}
			>
				<View style={styles.panels}>
					<SortablePanel
						section="selected"
						label="SELECTED COLUMNS"
						ids={selected}
						labelFor={labelFor}
						emptyMessage="Drop columns here to show them"
						moveIcon="arrow-forward"
						onMovePress={moveToAvailable}
					/>
					<SortablePanel
						section="available"
						label="AVAILABLE COLUMNS"
						ids={filteredAvailable}
						labelFor={labelFor}
						emptyMessage={search ? 'No columns match your search' : 'Drop columns here to hide them'}
						moveIcon="arrow-back"
						onMovePress={moveToSelected}
					/>
				</View>

				{/* Portal the DragOverlay to document.body so no ancestor
				    transform (FlexModal uses one for centering) can offset
				    its `position: fixed` coordinates. z-index is set higher
				    than react-native-paper's Modal (which uses ~1000-ish)
				    so the portalled overlay stays on top of the dialog. */}
				{createPortal(
					<DragOverlay
						dropAnimation={null}
						style={{ zIndex: 100000 }}
					>
						{activeId
							? <OverlayRow label={activeLabel} />
							: null}
					</DragOverlay>,
					document.body,
				)}
			</DndContext>

			<View style={styles.footer}>
				<Button mode="outlined" onPress={handleUseDefaults} testID="column-picker-use-defaults">
					Use Defaults
				</Button>
				<View style={styles.footerRight}>
					<Button mode="text" onPress={onDismiss} testID="column-picker-cancel">Cancel</Button>
					<Button mode="contained" onPress={handleApply} testID="column-picker-apply">Apply</Button>
				</View>
			</View>
			<ColumnPickerCloseConfirm
				visible={closeConfirmVisible}
				onCancel={() => setCloseConfirmVisible(false)}
				onDiscard={() => { setCloseConfirmVisible(false); onDismiss(); }}
				onApply={() => { setCloseConfirmVisible(false); handleApply(); }}
			/>
		</FlexModal>
	);
};

/* ------------------------------------------------------------------------ */

interface SortablePanelProps {
	section: Section;
	label: string;
	ids: string[];
	labelFor: (id: string) => string;
	emptyMessage: string;
	moveIcon: string;
	onMovePress: (id: string) => void;
}

const SortablePanel: React.FC<SortablePanelProps> = ({
	section, label, ids, labelFor, emptyMessage, moveIcon, onMovePress,
}) => {
	const theme = useTheme();
	const { setNodeRef, isOver } = useDroppable({ id: containerId(section) });

	const panelStyle: React.CSSProperties = {
		flex: 1,
		minWidth: 260,
		border: `1px solid ${theme.colors.surfaceVariant}`,
		borderRadius: 8,
		overflow: 'hidden',
		transition: 'background-color 120ms ease, border-color 120ms ease',
		...(isOver
			? {
				backgroundColor: 'rgba(0, 123, 255, 0.06)',
				borderColor: 'rgba(0, 123, 255, 0.6)',
			}
			: null),
	};

	return (
		<div ref={setNodeRef} style={panelStyle}>
			<View style={[styles.panelHeader, { backgroundColor: theme.colors.surfaceVariant }]}>
				<Text style={[styles.panelLabel, { color: theme.colors.onSurface }]}>{label}</Text>
				<View style={[styles.countBadge, { backgroundColor: theme.colors.tertiary }]}>
					<Text style={styles.countBadgeText}>{ids.length}</Text>
				</View>
			</View>
			<SortableContext items={ids} strategy={verticalListSortingStrategy}>
				<div style={{ minHeight: 80 }}>
					{ids.length === 0 ? (
						<DropHerePlaceholder message={emptyMessage} isOver={isOver} />
					) : (
						ids.map(id => (
							<SortableRow
								key={id}
								id={id}
								label={labelFor(id)}
								moveIcon={moveIcon}
								onMovePress={() => onMovePress(id)}
								testID={`column-picker-${section}-${id}`}
							/>
						))
					)}
				</div>
			</SortableContext>
		</div>
	);
};

/* ------------------------------------------------------------------------ */

const DropHerePlaceholder: React.FC<{ message: string; isOver: boolean }> = ({ message, isOver }) => {
	const theme = useTheme();
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: 80,
				margin: 8,
				borderRadius: 6,
				border: `2px dashed ${isOver ? 'rgba(0, 123, 255, 0.8)' : theme.colors.surfaceVariant}`,
				backgroundColor: isOver ? 'rgba(0, 123, 255, 0.08)' : 'transparent',
				transition: 'all 120ms ease',
			}}
		>
			<Text style={[
				styles.emptyText,
				{ color: isOver ? theme.colors.primary : theme.colors.onSurfaceVariant },
			]}>
				{isOver ? 'Drop Here' : message}
			</Text>
		</div>
	);
};

/* ------------------------------------------------------------------------ */

interface SortableRowProps {
	id: string;
	label: string;
	moveIcon: string;
	onMovePress: () => void;
	testID?: string;
}

const SortableRow: React.FC<SortableRowProps> = ({ id, label, moveIcon, onMovePress, testID }) => {
	const theme = useTheme();
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		// Source slot goes near-invisible while the DragOverlay carries the
		// real row. Reads as "the row has been lifted out of this spot."
		opacity: isDragging ? 0.35 : 1,
		backgroundColor: theme.colors.background,
	};

	// Only the grip handle starts a drag. The rest of the row (label +
	// arrow button) stays clickable / keyboard-focusable without the
	// drag-activation threshold swallowing events.
	const handleProps = { ...attributes, ...listeners };

	return (
		<div ref={setNodeRef} style={style} data-testid={testID}>
			<RowContent
				label={label}
				moveIcon={moveIcon}
				onMovePress={onMovePress}
				handleProps={handleProps}
				isDraggingHandle={isDragging}
			/>
		</div>
	);
};

interface RowContentProps {
	label: string;
	moveIcon?: string;
	onMovePress?: () => void;
	handleProps?: React.HTMLAttributes<HTMLDivElement>;
	isDraggingHandle?: boolean;
	elevated?: boolean;
}

/** Rendered both inline (as the sortable row content) and inside the
 *  DragOverlay (as the floating ghost), so they look identical. */
const RowContent: React.FC<RowContentProps> = ({
	label, moveIcon, onMovePress, handleProps, isDraggingHandle, elevated,
}) => {
	const theme = useTheme();
	return (
		<View
			style={[
				styles.row,
				{ borderBottomColor: theme.colors.surfaceVariant },
				elevated && styles.rowElevated,
			]}
		>
			{/* Drag handle — only this element has the drag listeners. */}
			<div
				{...(handleProps ?? {})}
				style={{
					cursor: isDraggingHandle ? 'grabbing' : 'grab',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: 4,
					touchAction: 'none',
				}}
				aria-label="Drag to reorder"
				role="button"
			>
				<Text style={[styles.gripIcon, { color: theme.colors.onSurfaceVariant }]}>
					{'\u2630'}
				</Text>
			</div>
			<Text style={[styles.rowLabel, { color: theme.colors.onSurface }]} numberOfLines={2}>
				{label}
			</Text>
			{moveIcon && onMovePress && (
				<IconButton
					icon={moveIcon}
					size={18}
					onPress={onMovePress}
					accessibilityLabel={`Move ${label} to the other list`}
				/>
			)}
		</View>
	);
};

const OverlayRow: React.FC<{ label: string }> = ({ label }) => (
	<div style={{ cursor: 'grabbing', pointerEvents: 'none' }}>
		<RowContent label={label} elevated />
	</div>
);

/* ------------------------------------------------------------------------ */

const styles = StyleSheet.create({
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.s,
	},
	title: {
		fontSize: 18,
		fontWeight: '700',
	},
	searchRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing.xs,
		marginBottom: spacing.m,
	},
	searchInput: {
		flex: 1,
	},
	panels: {
		flexDirection: 'row',
		gap: spacing.m,
		flexWrap: 'wrap',
	},
	panelHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing.xs,
		paddingHorizontal: spacing.s,
		paddingVertical: spacing.xs,
	},
	panelLabel: {
		fontSize: 12,
		fontWeight: '700',
		letterSpacing: 0.5,
	},
	countBadge: {
		minWidth: 22,
		paddingHorizontal: 6,
		paddingVertical: 2,
		borderRadius: 11,
		alignItems: 'center',
		justifyContent: 'center',
	},
	countBadgeText: {
		color: '#fff',
		fontSize: 11,
		fontWeight: '700',
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		borderBottomWidth: 1,
		paddingHorizontal: spacing.s,
		paddingVertical: 6,
		gap: spacing.xs,
		backgroundColor: '#fff',
	},
	rowElevated: {
		// Lift the overlay visually — shadow + slight scale communicate
		// "this is the thing under your cursor, detached from the list."
		borderRadius: 6,
		borderBottomWidth: 0,
		shadowColor: '#000',
		shadowOpacity: 0.2,
		shadowRadius: 8,
		shadowOffset: { width: 0, height: 4 },
		transform: [{ scale: 1.02 }],
	},
	rowLabel: {
		flex: 1,
		fontSize: 14,
	},
	gripIcon: {
		fontSize: 16,
		lineHeight: 16,
		opacity: 0.7,
	},
	emptyText: {
		fontSize: 13,
		fontStyle: 'italic',
		textAlign: 'center',
	},
	footer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: spacing.m,
		gap: spacing.s,
	},
	footerRight: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing.s,
	},
});
